import { Icon } from '@iconify/react';
import { TenancyStatusBadge, TenancyActions, formatDate, memberDisplayName, memberInitials } from './tenancyStatus';

const AVATAR_COLORS = [
    'tw-bg-blue-500', 'tw-bg-emerald-500', 'tw-bg-purple-500', 'tw-bg-pink-500',
    'tw-bg-orange-500', 'tw-bg-teal-500', 'tw-bg-indigo-500', 'tw-bg-rose-500',
    'tw-bg-cyan-500', 'tw-bg-amber-500', 'tw-bg-lime-600', 'tw-bg-fuchsia-500', 'tw-bg-violet-500'
];


const getAvatarColor = (id) => {
    return AVATAR_COLORS[(id || 0) % AVATAR_COLORS.length];
};

const getRoleLabel = (role) => {
    if (!role) return '';
    return role.charAt(0) + role.slice(1).toLowerCase();
};

const MemberCard = ({ member, onView, onEdit, onDelete, onToggleStatus, onHistory, showTenancy = false }) => {
    const fullName = memberDisplayName(member);
    const flatNumber = member.flat?.flat_number || '-';
    const wingTitle = member.flat?.wing?.title || '-';
    const flatType = member.flat?.flat_type || '';

    return (
        <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-100 tw-overflow-hidden hover:tw-shadow-md tw-transition-shadow">
            {/* Card Header */}
            <div className="tw-p-4 tw-pb-3">
                <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                    <div className="tw-flex tw-items-center tw-gap-3 tw-min-w-0 tw-flex-1">
                        <div className={`tw-w-11 tw-h-11 tw-rounded-full ${getAvatarColor(member.flat_ownership_id || member.occupant_id)} tw-flex tw-items-center tw-justify-center tw-flex-shrink-0`}>
                            <span className="tw-text-white tw-text-sm tw-font-semibold">
                                {memberInitials(member)}
                            </span>
                        </div>
                        <div className="tw-min-w-0">
                            <h4 className="tw-font-semibold tw-text-gray-800 tw-text-sm tw-truncate">{fullName}</h4>
                            <p className="tw-text-xs tw-text-gray-500">{getRoleLabel(member.role)}</p>
                        </div>
                    </div>
                    <div className="tw-flex tw-flex-col tw-items-end tw-gap-0.5 tw-flex-shrink-0">
                        <span className={`tw-px-2.5 tw-py-1 tw-rounded-full tw-text-[10px] tw-font-semibold tw-whitespace-nowrap ${
                            member.is_primary !== undefined
                                ? (member.is_primary ? 'tw-bg-blue-100 tw-text-blue-700' : 'tw-bg-gray-100 tw-text-gray-600')
                                : (member.is_resident ? 'tw-bg-emerald-100 tw-text-emerald-700' : 'tw-bg-gray-100 tw-text-gray-600')
                        }`}>
                            {member.is_primary !== undefined
                                ? (member.is_primary ? 'Primary' : 'Co-Owner')
                                : (member.is_resident ? 'Residing' : 'Non-Residing')
                            }
                        </span>
                        {member.ownership_share !== undefined && member.ownership_share !== null && (
                            <span className="tw-text-[10px] tw-text-gray-500">{parseFloat(member.ownership_share)}% share</span>
                        )}
                        {showTenancy && <TenancyStatusBadge member={member} />}
                    </div>
                </div>
            </div>

            {/* Card Body */}
            <div className="tw-px-4 tw-pb-3">
                {/* Unit Info */}
                <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-600 tw-mb-3 tw-pb-3 tw-border-b tw-border-gray-100">
                    <Icon icon="mdi:home-outline" className="tw-text-gray-400 tw-text-sm" />
                    <span className="tw-font-medium">{flatNumber}</span>
                    <span className="tw-text-gray-300">|</span>
                    <span>{wingTitle}{flatType ? ` - ${flatType}` : ''}</span>
                </div>

                {/* Contact Info */}
                <div className="tw-space-y-2">
                    {member.phone && (
                        <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-600">
                            <Icon icon="mdi:phone-outline" className="tw-text-gray-400 tw-text-sm tw-flex-shrink-0" />
                            <span>{member.phone}</span>
                        </div>
                    )}
                    {member.email && (
                        <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-600">
                            <Icon icon="mdi:email-outline" className="tw-text-gray-400 tw-text-sm tw-flex-shrink-0" />
                            <span className="tw-truncate">{member.email}</span>
                        </div>
                    )}
                </div>

                {/* Date Info */}
                <div className="tw-flex tw-items-center tw-justify-between tw-mt-3 tw-pt-3 tw-border-t tw-border-gray-100 tw-text-xs">
                    {member.agreement_end_date ? (
                        <div className="tw-flex tw-items-center tw-gap-1.5" title="Agreement end date">
                            <Icon icon="mdi:file-document-outline" className="tw-text-gray-400 tw-text-sm" />
                            <span className="tw-text-gray-600">{formatDate(member.agreement_end_date)}</span>
                        </div>
                    ) : <span />}
                    {member.start_date && (
                        <div className="tw-flex tw-items-center tw-gap-1.5" title={showTenancy ? 'Start date – end date' : 'Start date'}>
                            <Icon icon="mdi:calendar-outline" className="tw-text-gray-400 tw-text-sm" />
                            <span className="tw-text-gray-600">
                                {formatDate(member.start_date)}
                                {showTenancy && ` – ${member.end_date ? formatDate(member.end_date) : 'Present'}`}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Card Footer */}
            <div className="tw-flex tw-items-center tw-justify-end tw-px-4 tw-py-3 tw-bg-gray-50 tw-border-t tw-border-gray-100">
                <div className="tw-flex tw-items-center tw-gap-1">
                    <button onClick={() => onView?.(member)} className="tw-p-1.5 tw-rounded-md hover:tw-bg-white tw-text-gray-400 hover:tw-text-blue-600 tw-transition-colors" title="View">
                        <Icon icon="mdi:eye-outline" className="tw-text-base" />
                    </button>
                    <button onClick={() => onEdit?.(member)} className="tw-p-1.5 tw-rounded-md hover:tw-bg-white tw-text-gray-400 hover:tw-text-blue-600 tw-transition-colors" title="Edit">
                        <Icon icon="mdi:pencil-outline" className="tw-text-base" />
                    </button>
                    {showTenancy && (
                        <TenancyActions
                            member={member}
                            onToggleStatus={onToggleStatus}
                            onHistory={onHistory}
                            buttonClass="tw-p-1.5 tw-rounded-md tw-transition-colors"
                        />
                    )}
                    <button onClick={() => onDelete?.(member)} className="tw-p-1.5 tw-rounded-md hover:tw-bg-white tw-text-gray-400 hover:tw-text-red-600 tw-transition-colors" title="Delete">
                        <Icon icon="mdi:delete-outline" className="tw-text-base" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MemberCard;
